// Anexo 1 — Ficha Familiar (Fase 6, ver PLAN.md). Transcripción campo por campo
// del PDF oficial del Anexo 1 MSP (AM 00115-2021), páginas 272-275 (footer
// `SNS-MSP/HCU-Anexo1/2021`), bloques A-J; spec completa en
// `docs/hce-anexos/anexo1-ficha-familiar.md`.
//
// MODELO DE DATOS (decisión de Sue 2026-09-18, "modelo A"): la ficha es de una
// FAMILIA, pero se ancla al `Patient` del jefe de familia y los miembros son filas
// del formulario — sin `Group` FHIR ni cambios de AccessPolicy. Cada fila de miembro
// guarda cédula, parentesco y edad, lo que deja abierta una migración posterior a un
// `Group` (modelo C) sin perder datos. No hay recursos FHIR: sin consumidor
// demostrable, todo va como `Par[]` en el PDF firmado.
//
// Criterios de siempre: equivalencia funcional y nada que el papel no imprime.
// En particular NO existe en el papel APGAR familiar, ecomapa, escala ni fórmula de
// riesgo familiar (solo "calificación del riesgo familiar" libre en J).
// - Filas del papel (21 miembros, 5 embarazadas, 4 fallecidos, 17 de gestión del riesgo,
//   4 evaluaciones en I): se usan como TOPE de filas dinámicas; el papel no dice qué
//   hacer si la familia excede las impresas (se avisa el tope).
// - Bloque I: el papel imprime MARCAR / DETALLE (no SI/NO). "Marcar" es una casilla;
//   el rótulo del papel dice "agua insegura" (el instructivo dice "segura"): se usa el papel.
// - Familiograma (F) y croquis (G) son dibujo libre en el papel: equivalencia funcional
//   como texto, con la leyenda impresa de símbolos y abreviaturas como referencia.
// - Firma y sello del profesional (E) no son campos: el documento se firma con PAdES.
import {
  OPCIONES_SI_NO,
  paresDeCampos,
  textoDeCampo,
  valoresVacios,
  type CampoSimple,
  type OpcionCampo,
  type Valores,
} from './camposSimplesDatos';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
const opciones = (...etiquetas: readonly string[]): readonly OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));

/** Títulos de bloque como los imprime la hoja. */
export const TITULOS_ANEXO1 = {
  establecimiento: 'A. Datos del establecimiento y familia',
  miembros: 'B. Miembros de la familia',
  embarazadas: 'C. Embarazadas',
  mortalidad: 'D. Mortalidad familiar en los últimos 5 años',
  profesional: 'E. Datos del profesional responsable',
  familiograma: 'F. Familiograma',
  ubicacion: 'G. Ubicación de la vivienda y puntos de contaminación',
  tratamiento: 'H. Lugar o institución a la que acuden para tratamiento',
  riesgo: 'I. Evaluación del riesgo familiar',
  gestion: 'J. Evolución de la gestión del riesgo familiar',
} as const;

// ---------- A. Datos del establecimiento y familia ----------

export const CAMPOS_ESTABLECIMIENTO_ANEXO1: readonly CampoSimple[] = [
  // El instructivo da esta única lista (imprime "ISSFFA"; la institución es ISSFA).
  { clave: 'institucion', etiqueta: 'Institución del sistema', tipo: 'select', opciones: opciones('MSP', 'IESS', 'ISSFA', 'ISPOL') },
  { clave: 'unicodigo', etiqueta: 'Unicódigo' },
  { clave: 'establecimiento', etiqueta: 'Establecimiento de salud' },
  { clave: 'latitud', etiqueta: 'Georreferencia vivienda — latitud' },
  { clave: 'longitud', etiqueta: 'Georreferencia vivienda — longitud' },
  { clave: 'altitud', etiqueta: 'Georreferencia vivienda — altitud' },
  { clave: 'vulnerable', etiqueta: 'Vulnerable', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'notificadoA', etiqueta: 'Notificado a' },
  { clave: 'numeroFicha', etiqueta: 'Número de ficha familiar' },
];

export const CAMPOS_DIRECCION_ANEXO1: readonly CampoSimple[] = [
  { clave: 'parroquia', etiqueta: 'Parroquia' },
  { clave: 'canton', etiqueta: 'Cantón' },
  { clave: 'barrio', etiqueta: 'Barrio / sector / comunidad' },
  { clave: 'callePrincipal', etiqueta: 'Calle principal' },
  { clave: 'calleSecundaria', etiqueta: 'Calle secundaria' },
  { clave: 'numeroCasa', etiqueta: 'Número de casa' },
  { clave: 'referencia', etiqueta: 'Referencia del domicilio', tipo: 'area' },
];

export const CAMPOS_JEFE_ANEXO1: readonly CampoSimple[] = [
  { clave: 'primerApellido', etiqueta: 'Jefe de familia — primer apellido' },
  { clave: 'segundoApellido', etiqueta: 'Jefe de familia — segundo apellido' },
  { clave: 'primerNombre', etiqueta: 'Jefe de familia — primer nombre' },
  { clave: 'segundoNombre', etiqueta: 'Jefe de familia — segundo nombre' },
  // Campo libre en el papel: no imprime opciones.
  { clave: 'identificacionEtnica', etiqueta: 'Identificación étnica' },
  { clave: 'telefono', etiqueta: 'Número de teléfono' },
  { clave: 'fechaLlenado', etiqueta: 'Fecha de llenado', tipo: 'fecha' },
];

// ---------- B. Miembros de la familia (21 filas impresas) ----------

export const MAX_MIEMBROS_ANEXO1 = 21;

export const CAMPOS_MIEMBRO_ANEXO1: readonly CampoSimple[] = [
  { clave: 'apellidosNombres', etiqueta: 'Apellidos y nombres' },
  { clave: 'parentesco', etiqueta: 'Parentesco (con el jefe de familia)' },
  { clave: 'fechaNacimiento', etiqueta: 'Fecha de nacimiento', tipo: 'fecha' },
  numero('edad', 'Edad'),
  // H, D, M, A = horas, días, meses, años (instructivo).
  { clave: 'condicionEdad', etiqueta: 'Condición de la edad', tipo: 'select', opciones: [{ valor: 'H', etiqueta: 'H — Horas' }, { valor: 'D', etiqueta: 'D — Días' }, { valor: 'M', etiqueta: 'M — Meses' }, { valor: 'A', etiqueta: 'A — Años' }] },
  { clave: 'ocupacion', etiqueta: 'Ocupación' },
  { clave: 'sexo', etiqueta: 'Sexo', tipo: 'select', opciones: [{ valor: 'H', etiqueta: 'H — Hombre' }, { valor: 'M', etiqueta: 'M — Mujer' }] },
  // El instructivo da estos 5 niveles; lo que no aplica se deja en blanco (NA en el papel).
  { clave: 'nivelInstruccion', etiqueta: 'Nivel de instrucción', tipo: 'select', opciones: opciones('Inicial', 'Básica', 'Bachillerato', 'Superior', 'Postgrado') },
  { clave: 'vacunasCompletas', etiqueta: 'Vacunas completas', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'controlSaludBucal', etiqueta: 'Control de salud bucal (últimos 6 meses)', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'riesgoEnfermedadDiscapacidad', etiqueta: 'Riesgo, enfermedad, discapacidad' },
  {
    clave: 'grupoDispensarial',
    etiqueta: 'Grupo dispensarial',
    tipo: 'select',
    opciones: [
      { valor: '1', etiqueta: '1 — Sano' },
      { valor: '2', etiqueta: '2 — Riesgo' },
      { valor: '3', etiqueta: '3 — Enfermo' },
      { valor: '4', etiqueta: '4 — Discapacitado' },
    ],
  },
  { clave: 'numeroDocumento', etiqueta: 'Número de documento de identificación' },
  casilla('vulnerable', 'Vulnerable'),
];

// ---------- C. Embarazadas (5 filas) ----------

export const MAX_EMBARAZADAS_ANEXO1 = 5;

export const CAMPOS_EMBARAZADA_ANEXO1: readonly CampoSimple[] = [
  { clave: 'apellidosNombres', etiqueta: 'Apellidos y nombres' },
  { clave: 'fum', etiqueta: 'Fecha de última menstruación', tipo: 'fecha' },
  { clave: 'fpp', etiqueta: 'Fecha probable de parto', tipo: 'fecha' },
  numero('controlesMenos20', 'N° controles prenatales < 20 SG'),
  numero('controlesMas20', 'N° controles prenatales > 20 SG'),
  numero('semanasGestacion', 'Semanas de gestación'),
  { clave: 'esquemaVacunacion', etiqueta: 'Esquema de vacunación', tipo: 'select', opciones: opciones('Completa', 'Incompleta') },
  numero('gestas', 'Gestas'),
  numero('partos', 'Partos'),
  numero('abortos', 'Abortos'),
  numero('cesareas', 'Cesáreas'),
  { clave: 'antecedentes', etiqueta: 'Antecedentes patológicos obstétricos / observaciones' },
];

// ---------- D. Mortalidad familiar (4 filas) ----------

export const MAX_FALLECIDOS_ANEXO1 = 4;

export const CAMPOS_FALLECIDO_ANEXO1: readonly CampoSimple[] = [
  { clave: 'apellidosNombres', etiqueta: 'Apellidos y nombres' },
  { clave: 'parentesco', etiqueta: 'Parentesco' },
  { clave: 'edadAlFallecer', etiqueta: 'Edad al fallecer' },
  { clave: 'causa', etiqueta: 'Causa' },
];

// ---------- E. Profesional responsable (un solo nombre en el papel) ----------

export const CAMPOS_PROFESIONAL_ANEXO1: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha (aaaa-mm-dd)', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora (hh:mm)', tipo: 'hora' },
  { clave: 'primerNombre', etiqueta: 'Primer nombre' },
  { clave: 'primerApellido', etiqueta: 'Primer apellido' },
  { clave: 'segundoApellido', etiqueta: 'Segundo apellido' },
  { clave: 'numeroDocumento', etiqueta: 'Número de documento de identificación' },
];

// ---------- F. Familiograma / G. Ubicación / H. Lugar de tratamiento ----------

/** Leyenda impresa junto al familiograma: se muestra como referencia, no se captura. */
export const ABREVIATURAS_FAMILIOGRAMA_ANEXO1: readonly string[] = [
  'IMA - Infarto',
  'HTA - Hipertensión arterial',
  'DM - Diabetes',
  'CA - Cáncer',
  'EP - Epilepsia',
  'TB - Tuberculosis',
  'AS - Asma',
  'TBQ - Tabaquismo',
  'AL - Alcoholismo',
  'DD - Drogo dependiente',
  'DC - Discapacidad',
  'MG - Migrante',
  'EM - Embarazo',
  'X - Fallecido',
];

export const CAMPOS_FAMILIOGRAMA_ANEXO1: readonly CampoSimple[] = [{ clave: 'texto', etiqueta: 'Familiograma (descripción de la estructura familiar y relaciones)', tipo: 'area' }];
export const CAMPOS_UBICACION_ANEXO1: readonly CampoSimple[] = [{ clave: 'texto', etiqueta: 'Ubicación de la vivienda: dirección completa y puntos de posible contaminación (croquis)', tipo: 'area' }];
export const CAMPOS_TRATAMIENTO_ANEXO1: readonly CampoSimple[] = [{ clave: 'texto', etiqueta: 'Lugar o institución a la que acuden para tratamiento', tipo: 'area' }];

// ---------- I. Evaluación del riesgo familiar (19 componentes × 4 evaluaciones) ----------

export const MAX_EVALUACIONES_ANEXO1 = 4;

export interface ComponenteRiesgoAnexo1 {
  readonly numero: number;
  readonly grupo: string;
  readonly etiqueta: string;
  /** El 19 (Vulnerable — observaciones) solo tiene detalle, sin casilla MARCAR. */
  readonly soloDetalle?: boolean;
}

export const COMPONENTES_RIESGO_ANEXO1: readonly ComponenteRiesgoAnexo1[] = [
  { numero: 1, grupo: 'A. Riesgo biomédico', etiqueta: 'Personas con vacunación incompleta' },
  { numero: 2, grupo: 'A. Riesgo biomédico', etiqueta: 'Personas con malnutrición' },
  { numero: 3, grupo: 'A. Riesgo biomédico', etiqueta: 'Personas con enfermedades crónicas' },
  { numero: 4, grupo: 'A. Riesgo biomédico', etiqueta: 'Personas con enfermedades catastróficas / huérfanas' },
  { numero: 5, grupo: 'A. Riesgo biomédico', etiqueta: 'Embarazadas con riesgo' },
  { numero: 6, grupo: 'A. Riesgo biomédico', etiqueta: 'Personas con discapacidad' },
  { numero: 7, grupo: 'A. Riesgo biomédico', etiqueta: 'Personas con problemas mentales' },
  { numero: 8, grupo: 'B. Riesgo sanitario ambiental', etiqueta: 'Consumo de agua insegura' },
  { numero: 9, grupo: 'B. Riesgo sanitario ambiental', etiqueta: 'Incorrecta eliminación de basura y excretas' },
  { numero: 10, grupo: 'B. Riesgo sanitario ambiental', etiqueta: 'Incorrecta eliminación de desechos líquidos' },
  { numero: 11, grupo: 'B. Riesgo sanitario ambiental', etiqueta: 'Impacto ecológico por industrias' },
  { numero: 12, grupo: 'B. Riesgo sanitario ambiental', etiqueta: 'Animales intra domiciliarios' },
  { numero: 13, grupo: 'C. Riesgo socio económico', etiqueta: 'Desempleo del jefe de familia' },
  { numero: 14, grupo: 'C. Riesgo socio económico', etiqueta: 'Analfabetismo en la familia' },
  { numero: 15, grupo: 'C. Riesgo socio económico', etiqueta: 'Desestructuración familiar' },
  { numero: 16, grupo: 'C. Riesgo socio económico', etiqueta: 'Violencia / alcoholismo / drogadicción / tabaquismo' },
  { numero: 17, grupo: 'C. Riesgo socio económico', etiqueta: 'Malas condiciones de la vivienda' },
  { numero: 18, grupo: 'C. Riesgo socio económico', etiqueta: 'Hacinamiento' },
  { numero: 19, grupo: 'D. Vulnerable', etiqueta: 'Observaciones', soloDetalle: true },
];

/** Campos de UNA evaluación: la fecha + (marcar, detalle) por componente. */
export function camposEvaluacionAnexo1(): readonly CampoSimple[] {
  return [
    { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
    ...COMPONENTES_RIESGO_ANEXO1.flatMap((c) => [...(c.soloDetalle ? [] : [casilla(`m${c.numero}`, `${c.numero}. ${c.etiqueta} — marcar`)]), { clave: `d${c.numero}`, etiqueta: `${c.numero}. ${c.etiqueta} — detalle` }]),
  ];
}

// ---------- J. Evolución de la gestión del riesgo familiar (17 filas) ----------

export const MAX_GESTION_ANEXO1 = 17;

export const CAMPOS_GESTION_ANEXO1: readonly CampoSimple[] = [
  { clave: 'fechaAnalisis', etiqueta: 'Fecha de análisis', tipo: 'fecha' },
  // El papel solo dice "RIESGO" / "calificación del riesgo familiar": sin categorías ni regla.
  { clave: 'riesgo', etiqueta: 'Riesgo (calificación del riesgo familiar)' },
  { clave: 'compromisoFamilia', etiqueta: 'Compromiso de la familia' },
  { clave: 'compromisoEquipo', etiqueta: 'Compromiso del equipo de salud' },
  { clave: 'fechaEvaluacion', etiqueta: 'Fecha de evaluación', tipo: 'fecha' },
  { clave: 'cumplimiento', etiqueta: 'Evaluación del cumplimiento', tipo: 'select', opciones: opciones('Sí cumple', 'No cumple', 'Parcial') },
  { clave: 'causas', etiqueta: 'Causas del incumplimiento y observaciones' },
  { clave: 'responsable', etiqueta: 'Responsable' },
];

// ---------- Estado ----------

export interface EstadoAnexo1 {
  readonly establecimiento: Valores;
  readonly direccion: Valores;
  readonly jefe: Valores;
  readonly miembros: readonly Valores[];
  readonly embarazadas: readonly Valores[];
  readonly fallecidos: readonly Valores[];
  readonly profesional: Valores;
  readonly familiograma: Valores;
  readonly ubicacion: Valores;
  readonly tratamiento: Valores;
  readonly evaluaciones: readonly Valores[];
  readonly gestion: readonly Valores[];
}

export function estadoInicialAnexo1(): EstadoAnexo1 {
  return {
    establecimiento: valoresVacios(CAMPOS_ESTABLECIMIENTO_ANEXO1),
    direccion: valoresVacios(CAMPOS_DIRECCION_ANEXO1),
    jefe: valoresVacios(CAMPOS_JEFE_ANEXO1),
    miembros: [],
    embarazadas: [],
    fallecidos: [],
    profesional: valoresVacios(CAMPOS_PROFESIONAL_ANEXO1),
    familiograma: valoresVacios(CAMPOS_FAMILIOGRAMA_ANEXO1),
    ubicacion: valoresVacios(CAMPOS_UBICACION_ANEXO1),
    tratamiento: valoresVacios(CAMPOS_TRATAMIENTO_ANEXO1),
    evaluaciones: [],
    gestion: [],
  };
}

export const filaVaciaAnexo1 = (campos: readonly CampoSimple[]): Valores => valoresVacios(campos);

/** "Etiqueta: valor · Etiqueta: valor" de una fila — cadena vacía si no tiene ningún dato. */
export function resumenFilaAnexo1(campos: readonly CampoSimple[], fila: Valores): string {
  return campos
    .map((c) => {
      const texto = textoDeCampo(c, fila[c.clave]);
      return texto === '' ? '' : `${c.etiqueta}: ${texto}`;
    })
    .filter((x) => x !== '')
    .join(' · ');
}

const paresDeFilas = (titulo: string, rotulo: string, campos: readonly CampoSimple[], filas: readonly Valores[]): Par[] =>
  filas.map((f, i) => ({ pregunta: `${titulo} — ${rotulo} ${i + 1}`, respuesta: resumenFilaAnexo1(campos, f) }));

/** Serializa A-J a `Par[]` (filtra respuestas vacías). */
export function paresDeAnexo1(e: EstadoAnexo1): Par[] {
  const T = TITULOS_ANEXO1;
  const prefijo = (titulo: string): string => `${titulo} — `;
  const pares: Par[] = [
    ...paresDeCampos(CAMPOS_ESTABLECIMIENTO_ANEXO1, e.establecimiento, prefijo(T.establecimiento)),
    ...paresDeCampos(CAMPOS_DIRECCION_ANEXO1, e.direccion, prefijo(T.establecimiento)),
    ...paresDeCampos(CAMPOS_JEFE_ANEXO1, e.jefe, prefijo(T.establecimiento)),
    ...paresDeFilas(T.miembros, 'Miembro', CAMPOS_MIEMBRO_ANEXO1, e.miembros),
    ...paresDeFilas(T.embarazadas, 'Embarazada', CAMPOS_EMBARAZADA_ANEXO1, e.embarazadas),
    ...paresDeFilas(T.mortalidad, 'Fallecido', CAMPOS_FALLECIDO_ANEXO1, e.fallecidos),
    ...paresDeCampos(CAMPOS_PROFESIONAL_ANEXO1, e.profesional, prefijo(T.profesional)),
    ...paresDeCampos(CAMPOS_FAMILIOGRAMA_ANEXO1, e.familiograma, prefijo(T.familiograma)),
    ...paresDeCampos(CAMPOS_UBICACION_ANEXO1, e.ubicacion, prefijo(T.ubicacion)),
    ...paresDeCampos(CAMPOS_TRATAMIENTO_ANEXO1, e.tratamiento, prefijo(T.tratamiento)),
  ];
  e.evaluaciones.forEach((ev, i) => {
    const cabecera = `${T.riesgo} — Evaluación ${i + 1}${ev.fecha ? ` (${ev.fecha})` : ''}`;
    for (const c of COMPONENTES_RIESGO_ANEXO1) {
      const partes = [ev[`m${c.numero}`] === 'true' ? 'Marcado' : '', (ev[`d${c.numero}`] ?? '').trim()].filter((x) => x !== '');
      if (partes.length > 0) pares.push({ pregunta: `${cabecera} — ${c.numero}. ${c.etiqueta}`, respuesta: partes.join(' · ') });
    }
  });
  pares.push(...paresDeFilas(T.gestion, 'Fila', CAMPOS_GESTION_ANEXO1, e.gestion));
  return pares.filter((x) => x.respuesta.trim() !== '');
}
